import type { Pointer } from './platform';

export interface Insets { top: number; right: number; bottom: number; left: number }
export interface Rect { x: number; y: number; w: number; h: number }
export interface WindowMetrics {
  width: number; height: number; topInset: number;
  safeInsets?: Insets; menuRect?: Rect;
}
/** Layout units are independent of backing-store pixels and of artwork coordinates. */
export function viewport(metrics: WindowMetrics, reading = false) {
  const inset = metrics.safeInsets ?? { top: metrics.topInset, right: 0, bottom: 0, left: 0 };
  const available = Math.max(1, metrics.width - inset.left - inset.right);
  const tablet = available >= 600;
  const scale = tablet ? 1 : available / 390;
  const width = tablet ? (reading ? Math.min(available, 640) : available) : 390;
  const left = inset.left + (available - width * scale) / 2;
  const topPixels = Math.max(inset.top, tablet && metrics.menuRect ? metrics.menuRect.y + metrics.menuRect.h + 8 : 0);
  const top = topPixels / scale;
  const height = (metrics.height - inset.bottom) / scale;
  return { tablet, scale, width, height, left, top, bottom: inset.bottom / scale };
}
export type Viewport = ReturnType<typeof viewport>;
export function localPointer(pointer: Pointer, view: Viewport): Pointer {
  const point = (p: { x: number; y: number; id: number }) => ({ ...p, x: (p.x - view.left) / view.scale, y: p.y / view.scale - view.top });
  return { ...point(pointer), ...(pointer.touches ? { touches: pointer.touches.map(point) } : {}) };
}
export function deviceRect(rect: Rect, view: Viewport): Rect {
  return { x: view.left + rect.x * view.scale, y: (rect.y + view.top) * view.scale, w: rect.w * view.scale, h: rect.h * view.scale };
}
export function gridLayout(width: number, tablet: boolean, top: number, bottom: number, extraHeight = 72) {
  const gap = tablet ? 16 : 14;
  const columns = tablet ? Math.max(2, Math.min(4, Math.floor((width - 48 + gap) / (220 + gap)))) : 2;
  const cardWidth = (width - 48 - (columns - 1) * gap) / columns;
  const desired = tablet ? cardWidth + extraHeight : 210;
  const rows = tablet ? Math.max(1, Math.round((bottom - top + gap) / (desired + gap))) : 2;
  const cardHeight = Math.max(100, Math.min(desired, (bottom - top - (rows - 1) * gap) / rows));
  return { columns, rows, gap, cardWidth, cardHeight, capacity: columns * rows };
}
